import { useState, useRef, useEffect, useContext, useMemo } from "react";
import { API } from "./utils";
import { ThemeCtx } from "./AppShell";
import { useScreenCapture, fmtTime, bestAudioFmt } from "./EchoScreenCapture";
import { smartUpload, cancelSession, loadSession, clearSession, LARGE_FILE } from "./ChunkUploader";

// ─── HTML Editor (same as CaptureScreen) ─────────────────────────────────────
function HtmlEditor({ value, onChange, placeholder }) {
  const ref = useRef();
  const [focused, setFocused] = useState(false);
  const [af, setAf] = useState({});

  useEffect(()=>{ if(ref.current&&ref.current.innerHTML!==value) ref.current.innerHTML=value||""; },[]);

  const exec = (cmd,val=null)=>{ document.execCommand(cmd,false,val); ref.current.focus(); upd(); };
  const upd  = ()=>setAf({
    bold:document.queryCommandState("bold"),
    italic:document.queryCommandState("italic"),
    underline:document.queryCommandState("underline"),
    ul:document.queryCommandState("insertUnorderedList"),
    ol:document.queryCommandState("insertOrderedList"),
  });

  const TB2=({cmd,val=null,title,children})=>(
    <button onMouseDown={e=>{e.preventDefault();exec(cmd,val);}} title={title}
      style={{padding:"3px 6px",border:`1px solid ${af[cmd]?"#94A3B8":"#E5E7EB"}`,
        borderRadius:"4px",background:af[cmd]?"#E2E8F0":"#fff",cursor:"pointer",
        fontSize:"11px",fontWeight:"600",color:af[cmd]?"#1E293B":"#374151",
        minWidth:"26px",lineHeight:"16px"}}>
      {children}
    </button>
  );
  const Sep2=()=><div style={{width:"1px",height:"20px",background:"#E5E7EB",margin:"0 2px"}}/>;

  return(
    <div style={{border:`1px solid ${focused?"#94A3B8":"#E5E7EB"}`,borderRadius:"8px",overflow:"hidden"}}>
      <div style={{display:"flex",gap:"2px",padding:"5px 8px",background:"#F8FAFC",
        borderBottom:"1px solid #E5E7EB",flexWrap:"wrap",alignItems:"center",rowGap:"4px"}}>
        <select onMouseDown={e=>e.stopPropagation()}
          onChange={e=>{exec("formatBlock",e.target.value);e.target.value="";}} defaultValue=""
          style={{padding:"2px 4px",border:"1px solid #E5E7EB",borderRadius:"4px",fontSize:"11px",
            color:"#374151",background:"#fff",cursor:"pointer",outline:"none",fontFamily:"inherit",height:"24px"}}>
          <option value="" disabled>Format</option>
          <option value="p">Paragraph</option>
          <option value="h1">H1</option><option value="h2">H2</option><option value="h3">H3</option>
          <option value="blockquote">Quote</option><option value="pre">Code</option>
        </select>
        <Sep2/>
        <TB2 cmd="bold"      title="Bold (Ctrl+B)"><b>B</b></TB2>
        <TB2 cmd="italic"    title="Italic (Ctrl+I)"><i>I</i></TB2>
        <TB2 cmd="underline" title="Underline (Ctrl+U)"><u>U</u></TB2>
        <button onMouseDown={e=>{e.preventDefault();exec("strikeThrough");}}
          style={{padding:"3px 6px",border:"1px solid #E5E7EB",borderRadius:"4px",
            background:"#fff",cursor:"pointer",fontSize:"11px",color:"#374151"}}><s>S</s></button>
        <Sep2/>
        <TB2 cmd="insertUnorderedList" title="Bullet list">•≡</TB2>
        <TB2 cmd="insertOrderedList"   title="Numbered list">1≡</TB2>
        <Sep2/>
        <button onMouseDown={e=>{e.preventDefault();const u=prompt("URL:");if(u)exec("createLink",u);}}
          title="Insert link"
          style={{padding:"3px 6px",border:"1px solid #E5E7EB",borderRadius:"4px",
            background:"#fff",cursor:"pointer",fontSize:"11px",color:"#374151"}}>🔗</button>
        <button onMouseDown={e=>{e.preventDefault();exec("removeFormat");}}
          title="Clear formatting"
          style={{padding:"3px 6px",border:"1px solid #E5E7EB",borderRadius:"4px",
            background:"#fff",cursor:"pointer",fontSize:"11px",color:"#374151"}}>✕</button>
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning
        onFocus={()=>setFocused(true)} onBlur={()=>setFocused(false)}
        onKeyUp={upd} onMouseUp={upd}
        onInput={()=>onChange(ref.current.innerHTML)}
        data-placeholder={placeholder}
        style={{minHeight:"60px",maxHeight:"150px",overflowY:"auto",padding:"10px 12px",
          fontSize:"12px",color:"#111827",lineHeight:1.7,outline:"none",fontFamily:"inherit",
          background:"#fff"}}/>
      <style>{`
        [contenteditable]:empty:before{content:attr(data-placeholder);color:#9CA3AF;pointer-events:none;}
        [contenteditable] h1{font-size:1.6em;font-weight:800;margin:8px 0 4px;}
        [contenteditable] h2{font-size:1.3em;font-weight:700;margin:6px 0 3px;}
        [contenteditable] h3{font-size:1.1em;font-weight:700;margin:5px 0 2px;}
        [contenteditable] blockquote{border-left:3px solid #E5E7EB;padding-left:10px;color:#6B7280;margin:6px 0;}
        [contenteditable] pre{background:#F1F5F9;padding:8px;border-radius:5px;font-family:monospace;font-size:11px;}
        [contenteditable] a{color:#2563EB;text-decoration:underline;}
      `}</style>
    </div>
  );
}

// ─── Classify tabs (Tags / Company / Product / Indication) ───────────────────
function PillSelectPrimary({ items=[], value=[], onChange, labelKey, idKey, color }) {
  const [search, setSearch] = useState("");
  const filtered = items.filter(i=>(i[labelKey]||"").toLowerCase().includes(search.toLowerCase()));
  const isSelected = item => value.some(v=>v[idKey]===item[idKey]);
  const isPrimary  = item => value[0]?.[idKey]===item[idKey];
  const toggle = item => {
    const exists=value.find(v=>v[idKey]===item[idKey]);
    if(exists) onChange(value.filter(v=>v[idKey]!==item[idKey]));
    else       onChange([...value, item]);
  };
  const makePrimary = (item,e) => {
    e.stopPropagation();
    onChange([item,...value.filter(v=>v[idKey]!==item[idKey])]);
  };
  return(
    <div>
      {items.length>8&&<input value={search} onChange={e=>setSearch(e.target.value)}
        placeholder="Search…" style={{width:"100%",padding:"5px 9px",border:"1px solid #E5E7EB",
          borderRadius:"7px",fontSize:"11px",marginBottom:"6px",outline:"none",
          fontFamily:"inherit",boxSizing:"border-box"}}/>}
      <div style={{display:"flex",flexWrap:"wrap",gap:"4px"}}>
        {filtered.map(item=>{
          const sel=isSelected(item), pri=isPrimary(item);
          return(
            <button key={item[idKey]} onClick={()=>toggle(item)}
              style={{padding:"4px 10px",borderRadius:"20px",cursor:"pointer",outline:"none",
                border:`1.5px solid ${sel?color:"#E5E7EB"}`,background:sel?color:"#fff",
                color:sel?"#fff":"#374151",fontSize:"11px",fontWeight:sel?"700":"400",
                transition:"all .12s",display:"flex",alignItems:"center",gap:"3px",
                boxShadow:pri?`0 0 0 3px ${color}33`:"none"}}>
              {pri&&<span style={{fontSize:"9px"}}>★</span>}
              {item[labelKey]}
              {sel&&!pri&&<span onClick={e=>makePrimary(item,e)}
                style={{fontSize:"10px",opacity:0.55,marginLeft:"1px"}}>☆</span>}
            </button>
          );
        })}
        {!filtered.length&&<span style={{fontSize:"11px",color:"#9CA3AF"}}>No results</span>}
      </div>
      {value.length>1&&<div style={{fontSize:"9px",color:"#9CA3AF",marginTop:"4px"}}>
        ★ primary — click ☆ to change</div>}
    </div>
  );
}

function TagsPanel({ hashtags=[], keywords, setKeywords, color }) {
  const selectedTags=(keywords||"").split(",").map(s=>s.trim()).filter(Boolean);
  const toggle = tag => {
    if(selectedTags.includes(tag)) setKeywords(selectedTags.filter(t=>t!==tag).join(", "));
    else setKeywords([...selectedTags,tag].join(", "));
  };
  return(
    <div>
      {hashtags.length>0&&(
        <div style={{display:"flex",flexWrap:"wrap",gap:"4px",marginBottom:"6px"}}>
          {hashtags.map(tag=>{
            const active=selectedTags.includes(tag);
            return(
              <span key={tag} onClick={()=>toggle(tag)}
                style={{padding:"2px 8px",borderRadius:"20px",fontSize:"10px",fontWeight:"600",
                  cursor:"pointer",transition:"all .1s",
                  background:active?color:`${color}12`,color:active?"#fff":color,
                  border:`1px solid ${active?color:`${color}33`}`}}>
                {tag}
              </span>
            );
          })}
        </div>
      )}
      {selectedTags.length>0&&(
        <div style={{display:"flex",flexWrap:"wrap",gap:"4px"}}>
          {selectedTags.map(tag=>(
            <span key={tag} style={{display:"inline-flex",alignItems:"center",gap:"3px",
              padding:"2px 8px",borderRadius:"20px",fontSize:"10px",fontWeight:"600",
              background:`${color}12`,color,border:`1px solid ${color}33`}}>
              {tag}
              <span onClick={()=>setKeywords(selectedTags.filter(t=>t!==tag).join(", "))}
                style={{cursor:"pointer",opacity:0.6,fontSize:"10px",lineHeight:1}}>×</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ClassifyTabs({ bis, brands, setBrands, sponsors, setSponsors,
  indications, setIndications, keywords, setKeywords, hashtags=[], color }) {
  const TABS=[
    {id:"tags",       label:"Tags"},
    {id:"sponsor",    label:"Company"},
    {id:"brand",      label:"Product"},
    {id:"indication", label:"Indication"},
  ].filter(t=>{
    if(t.id==="tags")       return true;
    if(t.id==="sponsor")    return (bis?.SPONSOR||bis?.COMPANY||[]).length>0;
    if(t.id==="brand")      return (bis?.BRAND||[]).length>0;
    if(t.id==="indication") return (bis?.INDICATION||[]).length>0;
    return true;
  });
  const [tab,setTab]=useState("tags");
  const sponsorList = bis?.SPONSOR||bis?.COMPANY||[];
  return(
    <div style={{border:"1px solid #E5E7EB",borderRadius:"10px",overflow:"hidden"}}>
      <div style={{display:"flex",background:"#F8FAFC",borderBottom:"1px solid #E5E7EB"}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)}
            style={{flex:1,padding:"7px 4px",border:"none",background:"none",cursor:"pointer",
              fontSize:"11px",fontWeight:tab===t.id?"800":"500",
              color:tab===t.id?color:"#6B7280",
              borderBottom:`2px solid ${tab===t.id?color:"transparent"}`,
              transition:"all .12s"}}>
            {t.label}
            {t.id==="sponsor"    && sponsors.length>0    && <span style={{marginLeft:"3px",fontSize:"9px",fontWeight:"900",color}}>{sponsors.length}</span>}
            {t.id==="brand"      && brands.length>0      && <span style={{marginLeft:"3px",fontSize:"9px",fontWeight:"900",color}}>{brands.length}</span>}
            {t.id==="indication" && indications.length>0 && <span style={{marginLeft:"3px",fontSize:"9px",fontWeight:"900",color}}>{indications.length}</span>}
          </button>
        ))}
      </div>
      <div style={{padding:"10px 12px",minHeight:"20px"}}>
        {tab==="tags"&&<TagsPanel hashtags={hashtags} keywords={keywords} setKeywords={setKeywords} color={color}/>}
        {tab==="sponsor"&&sponsorList.length>0&&
          <PillSelectPrimary items={sponsorList} value={sponsors} onChange={setSponsors}
            labelKey="SPONSORNAME" idKey="SPONSORID" color={color}/>}
        {tab==="brand"&&(bis?.BRAND||[]).length>0&&
          <PillSelectPrimary items={bis.BRAND} value={brands} onChange={setBrands}
            labelKey="BRANDNAME" idKey="BRANDID" color={color}/>}
        {tab==="indication"&&(bis?.INDICATION||[]).length>0&&
          <PillSelectPrimary items={bis.INDICATION} value={indications} onChange={setIndications}
            labelKey="INDICATIONNAME" idKey="INDICATIONID" color={color}/>}
      </div>
    </div>
  );
}

const iStyle = {
  width:"100%", padding:"9px 12px", borderRadius:"8px", border:"1px solid #E5E7EB",
  outline:"none", fontFamily:"inherit", fontSize:"13px", color:"#111",
  background:"#fff", boxSizing:"border-box"
};
const SL = ({children, req}) => (
  <div style={{fontSize:"11px",fontWeight:"700",color:"#6B7280",textTransform:"uppercase",
    letterSpacing:".5px",marginBottom:"6px"}}>
    {children}{req&&<span style={{color:"#EF4444",marginLeft:"2px"}}>*</span>}
  </div>
);


// ─── Main page ────────────────────────────────────────────────────────────────
export default function EchoCapturePage({ user, subproject, onClose, onUploaded }) {
  const ctx   = useContext(ThemeCtx)||{};
  const color = ctx.theme?.primary||"#185FA5";

  // BIS
  const [bis,         setBis]         = useState(null);
  const [categories,  setCategories]  = useState([]);
  const [group,       setGroup]       = useState(null);
  const [brands,      setBrands]      = useState([]);
  const [sponsors,    setSponsors]    = useState([]);
  const [indications, setIndications] = useState([]);

  const isAdmin = subproject?.ROLE===1;

  // Form
  const [srcTab,    setSrcTab]    = useState("record");
  const [recMode,   setRecMode]   = useState(null);
  const startTriggerRef = useRef(null); // holds startRec from AudioRecorderWidget
  const notesRef = useRef(null);
  const [isDraft,   setIsDraft]   = useState(true);
  const [notify,    setNotify]    = useState(false);
  const [category,  setCategory]  = useState(null);
  const [subject,   setSubject]   = useState("");
  const [notes,     setNotes]     = useState("");
  const [keywords,  setKeywords]  = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [linkUrl,   setLinkUrl]   = useState("");
  const [file,      setFile]      = useState(null);
  const fileInputRef    = useRef(null);
  const [extraFiles,  setExtraFiles]  = useState([]);
  const extraInputRef   = useRef(null);

  // Audio recorder refs (per mode)
  const [audioBlob,    setAudioBlob]    = useState(null);
  const [pendingCat, setPendingCat] = useState(null);

  const resetAllRecording = () => {
    setRecMode(null);
    setAudioBlob(null);
    setRecKey(k=>k+1);
    screen.reset?.();        // clear screen audioBlob + screenshots
  };
  const [recKey,    setRecKey]    = useState(0); // force remount on mode change

  // Screen capture hook
  const screen = useScreenCapture({ user });

  // Upload
  const [uploading, setUploading] = useState(false);
  const [progress,  setProgress]  = useState(0);
  const [done,      setDone]      = useState(false);
  const [upErr,     setUpErr]     = useState("");
  const [doneMsg,   setDoneMsg]   = useState("");
  const [upStatus,  setUpStatus]  = useState("");
  const sessionRef  = useRef(null);
  // resume state — set when capture succeeds but file upload fails mid-way
  const [resumeState, setResumeState] = useState(null);
  // { docId, uploads, currentIndex, notify }

  // ── Load BIS ──────────────────────────────────────────────────────────────
  useEffect(()=>{
    if(!user?.USERID||!subproject?.SUBPROJECTID) return;
    fetch(`${API}/bis?userid=${user.USERID}&subprojectid=${subproject.SUBPROJECTID}`,
      {headers:{"Auth-Code":user.AUTH_CODE}})
      .then(r=>r.json())
      .then(res=>{
        const d=res.DATA||res;
        setBis(d);
        const cats=d.RECORD_CATEGORY||[];
        setCategories(cats);
        const first=cats.find(c=>(c.NAME||"").toLowerCase().includes("interview"))||cats[0];
        if(first){ setCategory(first); setKeywords(first.HASHTAGS?.trim()||""); }
        setGroup((d.GROUP||[]).find(g=>g.PRIMARY_GRP==="YES")||(d.GROUP||[])[0]||null);
      }).catch(()=>{});
  },[subproject?.SUBPROJECTID]);


  // ── can upload ────────────────────────────────────────────────────────────
  const canUpload = () => {
    if(!category||!subject.trim()) return false;
    if(srcTab==="record"){
      if(recMode==="screen") return !!(screen.audioBlob||screen.screenshots.length);
      return !!audioBlob;
    }
    if(srcTab==="url")  return !!linkUrl.trim();
    if(srcTab==="file") return !!file;
    return false;
  };

  // ── Upload files (used by both initial upload and resume) ────────────────
  const uploadFiles = async (docId, uploads, notify) => {
    let lastMsg = "";
    for(let i=0;i<uploads.length;i++){
      const u=uploads[i]; const isLast=i===uploads.length-1;
      const uNotify = isLast?(notify?"1":"0"):"0";
      const uAttach = isLast?"false":"true";

      if(u.videoUrl){
        const ufd=new FormData();
        ufd.append("userid",      user.USERID);
        ufd.append("documentid",  docId);
        ufd.append("attachment",  uAttach);
        ufd.append("notify",      uNotify);
        ufd.append("video_url",   u.videoUrl);
        const ures = await fetch(`${API}/upload_thumbs`,{method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:ufd});
        const uj = await ures.json();
        if(uj.MESSAGE && uj.MESSAGE.length > lastMsg.length) lastMsg = uj.MESSAGE;
      } else {
        const msg = await smartUpload({
          file:         u.file,
          userid:       user.USERID,
          subprojectid: subproject.SUBPROJECTID,
          documentid:   docId,
          authCode:     user.AUTH_CODE,
          screentime:   u.screentime||"0",
          isbookmarked: u.isbookmarked||"0",
          bookmark:     u.bookmark||"",
          attachment:   uAttach,
          notify:       uNotify,
          sessionRef,
          onStatus: (s) => setUpStatus(s),
          onProgress: (done, total) => {
            const base = 50 + Math.round(i/uploads.length*48);
            const seg  = Math.round(48/uploads.length);
            setProgress(base + Math.round(done/total*seg));
          },
        });
        if(msg && msg.length > lastMsg.length) lastMsg = msg;
      }
      setProgress(50+Math.round((i+1)/uploads.length*48));
    }
    return lastMsg;
  };

  // ── Continue interrupted upload ────────────────────────────────────────────
  const continueUpload = async () => {
    if(!resumeState) return;
    setResumeState(null);
    setUpErr("");
    setUploading(true);
    setProgress(50);
    try {
      const lastMsg = await uploadFiles(
        resumeState.docId,
        resumeState.uploads,
        resumeState.notify
      );
      setProgress(100); setDoneMsg(lastMsg); setDone(true);
      setTimeout(()=>{ onUploaded?.(); onClose?.(); },1800);
    } catch(e) {
      setUpErr(e.message||"Upload failed.");
      if(sessionRef.current) cancelSession(sessionRef.current, user.AUTH_CODE);
      // keep resumeState so user can try again
      setResumeState(resumeState);
    }
    setUploading(false);
  };
  const upload = async () => {
    if(!canUpload()||uploading) return;

    // stop screen share immediately — dismisses browser's "Stop sharing" popup
    if(recMode==="screen"){
      try{ screen.stop(); }catch{}
      // directly stop all tracks — guaranteed to dismiss the browser indicator
      try{ screen.streamRef?.current?.getTracks().forEach(t=>t.stop()); }catch{}
    }

    setUploading(true); setUpErr(""); setProgress(5);
    try {
      const fd=new FormData();
      fd.append("userid",       user.USERID);
      fd.append("subprojectid", subproject.SUBPROJECTID);
      fd.append("doctypeid",    category.DOCTYPEID);
      fd.append("title",        subject.trim());
      const stateid = isAdmin ? (isDraft?1:2) : 1;
      fd.append("stateid", String(stateid));
      const hasFile=(srcTab==="record")||(srcTab==="file"&&!!file);
      const hasUploads = hasFile || (srcTab==="url"&&!!linkUrl.trim());
      fd.append("attachment", hasFile?"true":"false");
      fd.append("notify", hasUploads?"0":(notify?"1":"0"));
      if(notes.trim())     fd.append("notes",           notes.trim());
      if(keywords.trim())  fd.append("keywords",        keywords.trim());
      if(sourceUrl.trim()) fd.append("tidentification", sourceUrl.trim());
      if(group)            fd.append("groupid",         group.GROUPID);
      brands.forEach((b,i)=>fd.append("brandid",JSON.stringify({brandid:b.BRANDID,primary:i===0?1:0})));
      sponsors.forEach((s,i)=>fd.append("sponsorid",JSON.stringify({sponsorid:s.SPONSORID,primary:i===0?1:0})));
      indications.forEach((n,i)=>fd.append("indicationid",JSON.stringify({indicationid:n.INDICATIONID,primary:i===0?1:0})));

      setProgress(25);
      const r=await fetch(`${API}/capture`,{method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      const res=await r.json();
      if(res.STATUS!=="1"&&res.STATUS!==1) throw new Error(res.MESSAGE||"Capture failed");
      const docId=res.DATA?.[0]?.DOCUMENTID;
      let lastMsg = res.MESSAGE||""; // capture message as fallback if no uploads
      setProgress(50);

      if(docId) {
        const uploads=[];
        const aBlob = recMode==="screen" ? screen.audioBlob : audioBlob;
        if(srcTab==="record" && aBlob){
          const audioType = "audio/webm";
          const fname = `echo_${recMode}_${Date.now()}.webm`;
          uploads.push({ file:new File([aBlob], fname, {type:audioType}), screentime:"0", isbookmarked:"0", bookmark:"" });
        } else if(srcTab==="file" && file){
          uploads.push({ file, screentime:"0", isbookmarked:"0", bookmark:"" });
        } else if(srcTab==="url" && linkUrl.trim()){
          uploads.push({ videoUrl: linkUrl.trim() });
        }
        screen.screenshots.forEach(ss => uploads.push({
          file: ss.file,
          screentime:   ss.screentime  || "0",
          isbookmarked: ss.isBookmark  ? "1" : "0",
          bookmark:     ss.bookmarkText|| ""
        }));
        extraFiles.forEach(f => uploads.push({ file:f, screentime:"0", isbookmarked:"0", bookmark:"" }));

        try {
          const lastMsg = await uploadFiles(docId, uploads, notify);
          setProgress(100); setDoneMsg(lastMsg); setDone(true);
          setTimeout(()=>{ onUploaded?.(); onClose?.(); },1800);
        } catch(e) {
          // capture succeeded but file upload failed — save for resume
          setResumeState({ docId, uploads, notify });
          throw e;
        }
      } else {
        setProgress(100); setDoneMsg(res.MESSAGE||""); setDone(true);
        setTimeout(()=>{ onUploaded?.(); onClose?.(); },1800);
      }
    }catch(e){
      setUpErr(e.message||"Upload failed.");
      if(sessionRef.current) cancelSession(sessionRef.current, user.AUTH_CODE);
    }
    setUploading(false);
  };

  const MODES=[
    {id:"mic",    l:"🎤 Mic",      d:"Microphone only"},
    {id:"system", l:"🔊 Speaker",  d:"Tab/window speaker audio"},
    {id:"both",   l:"🎤+🔊 Both", d:"Mic + speaker audio mixed"},
    {id:"screen", l:"🖥 Screen+",  d:"Video · Screenshot · Bookmark · Audio"},
  ];

  // Category → allowed modes
  const CATEGORY_MODES = {
    "interview": ["mic","system"],   // Mic + Speaker
    "notes":     ["mic"],            // Mic only
    "earnings":  ["system","screen"],// Speaker + Screen+
    "news":      ["system","screen"],// Speaker + Screen+
    "webinar":   ["system","screen"],// Speaker + Screen+
    "webinars":  ["system","screen"],
  };
  const allowedModes = useMemo(()=>{
    if(!category) return MODES.map(m=>m.id);
    const key=(category.NAME||"").toLowerCase();
    const match=Object.entries(CATEGORY_MODES).find(([k])=>key.includes(k));
    return match?match[1]:MODES.map(m=>m.id);
  // eslint-disable-next-line
  },[category?.NAME]);
  const [previewUrl, setPreviewUrl] = useState(null);

  const screenBlobUrl = screen.audioBlob ? URL.createObjectURL(screen.audioBlob) : null;

  return(
    <div style={{position:"fixed",top:"48px",left:"200px",right:0,bottom:0,
      zIndex:950,display:"flex",flexDirection:"column",overflow:"hidden",background:"#F8FAFC"}}>

      {/* screen overlay rendered by hook */}
      {screen.overlay}

      {/* screenshot preview modal */}
      {previewUrl&&(
        <div onClick={()=>setPreviewUrl(null)}
          style={{position:"fixed",inset:0,zIndex:2000,background:"rgba(0,0,0,.85)",
            display:"flex",alignItems:"center",justifyContent:"center",cursor:"zoom-out"}}>
          <div style={{position:"relative",maxWidth:"90vw",maxHeight:"90vh"}}>
            <img src={previewUrl} alt="preview"
              style={{maxWidth:"90vw",maxHeight:"90vh",borderRadius:"8px",
                display:"block",boxShadow:"0 24px 64px rgba(0,0,0,.6)"}}/>
            <button onClick={()=>setPreviewUrl(null)}
              style={{position:"absolute",top:"-14px",right:"-14px",
                width:"30px",height:"30px",borderRadius:"50%",border:"none",
                background:"#fff",color:"#111",fontSize:"16px",cursor:"pointer",
                display:"flex",alignItems:"center",justifyContent:"center",
                boxShadow:"0 2px 8px rgba(0,0,0,.3)",fontWeight:"700"}}>×</button>
          </div>
        </div>
      )}

      {/* header */}
      <div style={{flexShrink:0,background:color,color:"#fff",
        padding:"12px 24px",display:"flex",alignItems:"center",gap:"14px"}}>
        <button onClick={onClose}
          style={{background:"rgba(255,255,255,.2)",border:"none",cursor:"pointer",
            color:"#fff",borderRadius:"50%",width:"30px",height:"30px",
            display:"flex",alignItems:"center",justifyContent:"center",fontSize:"16px"}}>←</button>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
          <path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07"/>
        </svg>
        <div style={{flex:1}}>
          <div style={{fontSize:"16px",fontWeight:"600"}}>Echo Capture</div>
          <div style={{fontSize:"11px",opacity:.8}}>{subproject?.SUMMARY}</div>
        </div>
        <button onClick={upload} disabled={!canUpload()||uploading||done}
          style={{padding:"8px 20px",borderRadius:"8px",border:"none",fontFamily:"inherit",
            fontSize:"13px",fontWeight:"700",
            cursor:canUpload()&&!uploading&&!done?"pointer":"not-allowed",
            background:canUpload()&&!uploading&&!done?"rgba(255,255,255,.9)":"rgba(255,255,255,.3)",
            color:canUpload()&&!uploading&&!done?color:"rgba(255,255,255,.5)",
            display:"flex",alignItems:"center",gap:"6px"}}>
          {uploading
            ?<><div style={{width:"13px",height:"13px",border:"2px solid rgba(255,255,255,.4)",
                borderTopColor:"#fff",borderRadius:"50%",animation:"spin .8s linear infinite"}}/>
              {progress}%</>
            :done?"✓ Done"
            :<><svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
              </svg>Upload to Echo</>}
        </button>
      </div>

      {uploading&&<div style={{flexShrink:0}}>
        <div style={{height:"3px",background:`${color}33`}}>
          <div style={{height:"100%",width:`${progress}%`,background:color,transition:"width .4s"}}/></div>
        {upStatus&&<div style={{padding:"4px 16px",fontSize:"11px",color:color,background:`${color}08`,
          borderBottom:`1px solid ${color}22`}}>{upStatus}</div>}
      </div>}
      {done&&<div style={{flexShrink:0,padding:"10px 24px",background:"#D1FAE5",
        borderBottom:"1px solid #A7F3D0",fontSize:"13px",fontWeight:"600",color:"#065F46"}}>
        ✅ {doneMsg}</div>}
      {upErr&&<div style={{flexShrink:0,padding:"10px 24px",background:"#FEF2F2",
        borderBottom:"1px solid #FECACA",fontSize:"13px",color:"#B91C1C",
        display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
        <span>{upErr}</span>
        {resumeState&&(
          <button onClick={continueUpload}
            style={{flexShrink:0,padding:"6px 16px",borderRadius:8,border:"none",
              background:"#EF4444",color:"#fff",fontSize:12,fontWeight:700,
              cursor:"pointer",whiteSpace:"nowrap"}}>
            Continue Upload
          </button>
        )}
      </div>}

      {/* body */}
      <div style={{flex:1,overflow:"hidden",display:"flex"}}>

        {/* LEFT — source */}
        <div style={{flex:"0 0 420px",borderRight:"1px solid #E5E7EB",
          overflowY:"auto",padding:"20px",display:"flex",flexDirection:"column",gap:"16px"}}>

          {/* Category switch warning */}
          {pendingCat&&(
            <div style={{padding:"10px 14px",borderRadius:"10px",background:"#FEF3C7",
              border:"1px solid #F59E0B",marginBottom:"8px"}}>
              <div style={{fontSize:"12px",fontWeight:"700",color:"#92400E",marginBottom:"6px"}}>
                ⚠ Switching to "{pendingCat.NAME}" will discard your current recording.
              </div>
              <div style={{display:"flex",gap:"8px"}}>
                <button onClick={()=>{
                    setCategory(pendingCat); setKeywords(pendingCat.HASHTAGS?.trim()||"");
                    resetAllRecording(); setPendingCat(null);
                  }}
                  style={{padding:"5px 14px",borderRadius:"20px",border:"none",
                    background:"#D97706",color:"#fff",fontSize:"12px",fontWeight:"700",cursor:"pointer"}}>
                  Yes, discard
                </button>
                <button onClick={()=>setPendingCat(null)}
                  style={{padding:"5px 14px",borderRadius:"20px",border:"1px solid #D97706",
                    background:"#fff",color:"#92400E",fontSize:"12px",fontWeight:"600",cursor:"pointer"}}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* category */}
          <div>
            <SL req>Category</SL>
            <div style={{display:"flex",gap:"7px",flexWrap:"wrap"}}>
              {categories.map(cat=>{
                const sel=category?.ID===cat.ID;
                return(
                  <button key={cat.ID}
                    onClick={()=>{
                      const hasRecording = recMode==="screen"
                        ? !!(screen.audioBlob||screen.screenshots.length)
                        : !!audioBlob;
                      if(hasRecording && category?.ID!==cat.ID){
                        setPendingCat(cat); return;
                      }
                      setCategory(cat); setKeywords(cat.HASHTAGS?.trim()||"");
                      resetAllRecording();
                    }}
                    style={{padding:"6px 14px",borderRadius:"20px",cursor:"pointer",
                      fontFamily:"inherit",fontSize:"12px",fontWeight:"700",
                      border:`2px solid ${sel?cat.COLOUR:"#E5E7EB"}`,
                      background:sel?cat.COLOUR:"#fff",
                      color:sel?"#fff":"#374151",transition:"all .15s"}}>
                    {cat.NAME}
                  </button>
                );
              })}
            </div>
          </div>

          {/* source tabs */}
          <div>
            <SL req>Source</SL>
            <div style={{display:"flex",background:"#F3F4F6",borderRadius:"9px",
              padding:"3px",marginBottom:"14px"}}>
              {[{id:"record",l:"🎙 Record"},{id:"url",l:"🔗 URL"},{id:"file",l:"📁 File"}].map(t=>(
                <button key={t.id}
                  onClick={()=>{ setSrcTab(t.id); setAudioBlob(null); setFile(null); setLinkUrl(""); }}
                  style={{flex:1,padding:"7px 4px",borderRadius:"7px",border:"none",
                    cursor:"pointer",fontFamily:"inherit",fontSize:"12px",fontWeight:"600",
                    background:srcTab===t.id?"#fff":"transparent",
                    color:srcTab===t.id?color:"#6B7280",
                    boxShadow:srcTab===t.id?"0 1px 4px rgba(0,0,0,.1)":"none",
                    transition:"all .15s"}}>
                  {t.l}
                </button>
              ))}
            </div>

            {/* RECORD */}
            {srcTab==="record"&&(
              <div>
                {/* Mode buttons */}
                <div style={{display:"flex",gap:"8px",flexWrap:"wrap",marginBottom:"14px",justifyContent:"center"}}>
                  {MODES.filter(m=>allowedModes.includes(m.id)).map(m=>{
                    const sel=recMode===m.id;
                    return(
                      <button key={m.id}
                        onClick={()=>{
                          setRecMode(m.id); setAudioBlob(null); setRecKey(k=>k+1); if(m.id!=="screen") screen.reset?.();
                          if(m.id==="screen"){ setTimeout(()=>screen.start(),50); }
                          else { setTimeout(()=>startTriggerRef.current?.(),80); }
                        }}
                        style={{padding:"6px 14px",borderRadius:"10px",
                          fontFamily:"inherit",fontSize:"12px",fontWeight:"700",
                          outline:sel?`3px solid ${color}44`:`3px solid #E5E7EB`,
                          outlineOffset:"2px",
                          border:sel?`2px solid ${color}`:`2px solid #CBD5E1`,
                          background:sel?`linear-gradient(135deg,${color},${color}CC)`:"#fff",
                          color:sel?"#fff":"#475569",
                          cursor:"pointer",
                          display:"flex",flexDirection:"column",alignItems:"center",gap:"2px",
                          minWidth:"68px",
                          boxShadow:sel?`0 3px 10px ${color}44`:"0 1px 3px rgba(0,0,0,0.06)"}}>
                        <span style={{fontSize:"16px",lineHeight:1}}>
                          {m.id==="mic"?"🎤":m.id==="system"?"🔊":m.id==="both"?"🎙":"🖥"}
                        </span>
                        <span style={{fontSize:"10px",letterSpacing:"0.2px"}}>
                          {m.id==="mic"?"Mic":m.id==="system"?"Speaker":m.id==="both"?"Mic+Spk":"Screen+"}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* recorder — mounts when mode selected, exposes startRec via ref */}
                {recMode&&recMode!=="screen"&&(
                  <AudioRecorderWidget key={recKey} mode={recMode} color={color}
                    startTrigger={startTriggerRef} onBlob={setAudioBlob} user={user}/>
                )}

                {/* screen+ opens directly on button click */}

                {/* screen+ results */}
                {recMode==="screen"&&!screen.active&&(screen.audioBlob||screen.screenshots.length>0)&&(
                  <div style={{marginTop:"12px",display:"flex",flexDirection:"column",gap:"8px"}}>
                    {screen.audioBlob&&screenBlobUrl&&(
                      <div style={{padding:"10px",background:"#F0FDF4",borderRadius:"8px",
                        border:"1px solid #A7F3D0"}}>
                        <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"6px"}}>
                          <span style={{fontSize:"12px",color:"#059669",fontWeight:"700",flex:1}}>✓ Audio recorded · {fmtTime(screen.audioDur||0)}</span>
                          <button onClick={()=>{
                            const ext=screen.audioBlob.type.includes("mp4")?"mp4":"webm";
                            const url=URL.createObjectURL(screen.audioBlob);
                            const a=document.createElement("a"); a.href=url;
                            a.download=`echo_screen_${Date.now()}.${ext}`;
                            document.body.appendChild(a);a.click();document.body.removeChild(a);
                            setTimeout(()=>URL.revokeObjectURL(url),1000);
                          }} style={{padding:"3px 10px",borderRadius:"20px",border:"none",
                            background:"#059669",color:"#fff",cursor:"pointer",
                            fontFamily:"inherit",fontSize:"11px",fontWeight:"600"}}>⬇ Save</button>
                        </div>
                        <audio controls src={screenBlobUrl} style={{width:"100%"}}/>
                        {/* Generate Transcript from screen recording */}
                        <div style={{marginTop:"8px"}}>
                          <GenerateTranscriptBtn
                            getBlob={()=>Promise.resolve(screen.audioBlob)}
                            user={user} color={color}/>
                        </div>
                                      </div>
                    )}
                    {screen.screenshots.length>0&&(
                      <div>
                        <div style={{fontSize:"11px",fontWeight:"700",color:"#6B7280",
                          textTransform:"uppercase",letterSpacing:".4px",marginBottom:"6px"}}>
                          Captures ({screen.screenshots.length})</div>
                        {screen.screenshots.map((ss,i)=>(
                          <div key={i} style={{display:"flex",alignItems:"center",gap:"8px",
                            padding:"7px 10px",background:"#fff",borderRadius:"8px",
                            border:"1px solid #E5E7EB",marginBottom:"4px"}}>
                            <img src={URL.createObjectURL(ss.file)} alt=""
                              onClick={()=>setPreviewUrl(URL.createObjectURL(ss.file))}
                              style={{width:"52px",height:"34px",objectFit:"cover",borderRadius:"4px",flexShrink:0,cursor:"zoom-in"}}/>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontSize:"11px",fontWeight:"600",color:"#111"}}>
                                {ss.isBookmark?"🔖 "+ss.bookmarkText:"📸 Screenshot"}</div>
                              <div style={{fontSize:"10px",color:"#9CA3AF"}}>⏱ {ss.screentime}</div>
                            </div>
                            <button onClick={()=>screen.downloadShot(ss)}
                              style={{background:"none",border:"none",cursor:"pointer",color:"#6B7280",fontSize:"14px",padding:"2px"}}>⬇</button>
                          </div>
                        ))}
                      </div>
                    )}
                    <button onClick={screen.start}
                      style={{padding:"8px",borderRadius:"8px",border:`1px solid ${color}`,
                        background:`${color}10`,color,cursor:"pointer",fontFamily:"inherit",
                        fontSize:"12px",fontWeight:"600"}}>
                      + Re-open Capture View</button>
                  </div>
                )}
              </div>
            )}

            {/* URL */}
            {srcTab==="url"&&(
              <div>
                <input value={linkUrl} onChange={e=>setLinkUrl(e.target.value)}
                  placeholder="https://youtube.com/watch?v=…" style={iStyle}/>
                <div style={{fontSize:"11px",color:"#9CA3AF",marginTop:"4px"}}>YouTube, Vimeo, or direct audio/video URL</div>
              </div>
            )}

            {/* FILE */}
            {srcTab==="file"&&(
              <div>
                <div onClick={()=>fileInputRef.current?.click()}
                  onDragOver={e=>e.preventDefault()}
                  onDrop={e=>{e.preventDefault();const f=e.dataTransfer.files[0];if(f)setFile(f);}}
                  style={{border:"2px dashed #E5E7EB",borderRadius:"12px",padding:"28px",
                    textAlign:"center",cursor:"pointer",background:file?"#F0FDF4":"#FAFAFA"}}>
                  {file?(
                    <>
                      <div style={{fontSize:"28px",marginBottom:"6px"}}>🎵</div>
                      <div style={{fontSize:"13px",fontWeight:"600",color:"#111",marginBottom:"3px"}}>{file.name}</div>
                      <div style={{fontSize:"12px",color:"#6B7280",marginBottom:"10px"}}>{(file.size/1024/1024).toFixed(1)} MB</div>
                      <button onClick={e=>{e.stopPropagation();setFile(null);}}
                        style={{padding:"5px 14px",borderRadius:"20px",border:"none",
                          background:"#FEE2E2",color:"#991B1B",cursor:"pointer",fontFamily:"inherit",fontSize:"12px"}}>Remove</button>
                    </>
                  ):(
                    <>
                      <div style={{fontSize:"28px",marginBottom:"8px"}}>📁</div>
                      <div style={{fontSize:"14px",color:"#6B7280"}}>Drop file or <span style={{color,fontWeight:"700"}}>browse</span></div>
                      <div style={{fontSize:"11px",color:"#9CA3AF",marginTop:"4px"}}>MP3, MP4, WAV, M4A, WebM</div>
                    </>
                  )}
                </div>
                <input ref={fileInputRef} type="file"
                  accept="audio/*,video/*,.mp3,.mp4,.wav,.m4a,.webm,.ogg"
                  style={{display:"none"}}
                  onChange={e=>{if(e.target.files[0])setFile(e.target.files[0]);}}/>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — metadata */}
        <div style={{flex:1,overflowY:"auto",padding:"3px 16px",display:"flex",flexDirection:"column",gap:"4px"}}>
          <div>
            <SL req>Title</SL>
            <input value={subject} onChange={e=>setSubject(e.target.value)}
              placeholder="e.g. ASCO 2025 Keynote Recording" style={iStyle}/>
          </div>
          <div>
            <SL>Classify</SL>
            <ClassifyTabs bis={bis}
              brands={brands} setBrands={setBrands}
              sponsors={sponsors} setSponsors={setSponsors}
              indications={indications} setIndications={setIndications}
              keywords={keywords} setKeywords={setKeywords}
              hashtags={(keywords||"").split(",").map(s=>s.trim()).filter(Boolean)}
              color={color}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"12px"}}>
            <div><SL>Source URL</SL>
              <input value={sourceUrl} onChange={e=>setSourceUrl(e.target.value)}
                placeholder="https://source.com" style={iStyle}/></div>
            <div><SL>Group</SL>
              <select value={group?.GROUPID||""}
                onChange={e=>setGroup((bis?.GROUP||[]).find(g=>String(g.GROUPID)===e.target.value)||null)}
                style={{...iStyle,cursor:"pointer"}}>
                <option value="">None</option>
                {(bis?.GROUP||[]).map(g=><option key={g.GROUPID} value={g.GROUPID}>{g.GROUPNAME}</option>)}
              </select></div>
          </div>
          <div><SL>Notes</SL>
            <HtmlEditor value={notes} onChange={setNotes}
              placeholder="Key findings, summary…"/></div>

          {/* Extra attachments */}
          <div>
            <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"6px"}}>
              <SL>Additional Attachments</SL>
              <button onClick={()=>extraInputRef.current?.click()}
                style={{padding:"3px 10px",borderRadius:"20px",border:`1px solid ${color}`,
                  background:`${color}10`,color,cursor:"pointer",fontFamily:"inherit",
                  fontSize:"11px",fontWeight:"600",marginBottom:"6px"}}>
                + Add File
              </button>
              <input ref={extraInputRef} type="file" multiple style={{display:"none"}}
                onChange={e=>{ if(e.target.files) setExtraFiles(p=>[...p,...Array.from(e.target.files)]); }}/>
            </div>
            {extraFiles.length>0&&(
              <div style={{display:"flex",flexDirection:"column",gap:"4px"}}>
                {extraFiles.map((f,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"center",gap:"8px",
                    padding:"6px 10px",background:"#F8FAFC",borderRadius:"7px",
                    border:"1px solid #E5E7EB"}}>
                    <span style={{fontSize:"12px"}}>📎</span>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:"12px",fontWeight:"500",color:"#111",
                        overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.name}</div>
                      <div style={{fontSize:"10px",color:"#9CA3AF"}}>{(f.size/1024/1024).toFixed(1)} MB</div>
                    </div>
                    <button onClick={()=>setExtraFiles(p=>p.filter((_,j)=>j!==i))}
                      style={{background:"none",border:"none",cursor:"pointer",
                        color:"#9CA3AF",fontSize:"16px",lineHeight:1,padding:0}}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Publish / Draft + Notify — admin only */}
          {isAdmin&&(
            <div style={{display:"flex",gap:"10px",alignItems:"flex-start"}}>
              <div style={{flex:1}}>
                <SL>Status</SL>
                <div style={{display:"flex",background:"#F1F5F9",borderRadius:"8px",
                  padding:"3px",gap:"2px"}}>
                  {[{v:false,l:"🚀 Publish"},{v:true,l:"📝 Draft"}].map(opt=>(
                    <button key={String(opt.v)} onClick={()=>setIsDraft(opt.v)}
                      style={{flex:1,padding:"6px 8px",borderRadius:"6px",border:"none",
                        cursor:"pointer",fontFamily:"inherit",fontSize:"11px",fontWeight:"700",
                        transition:"all .12s",
                        background:isDraft===opt.v?(opt.v?"#64748B":color):"transparent",
                        color:isDraft===opt.v?"#fff":"#6B7280"}}>
                      {opt.l}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{flex:1}}>
                <SL>Notification</SL>
                <div style={{display:"flex",gap:"6px"}}>
                  {[{v:true,icon:"🔔",label:"Notify"},{v:false,icon:"🔕",label:"Silent"}].map(opt=>(
                    <button key={String(opt.v)} onClick={()=>setNotify(opt.v)}
                      style={{flex:1,padding:"7px",borderRadius:"8px",cursor:"pointer",
                        outline:"none",fontFamily:"inherit",
                        border:`1.5px solid ${notify===opt.v?color:"#E5E7EB"}`,
                        background:notify===opt.v?`${color}08`:"#FAFAFA",
                        display:"flex",alignItems:"center",justifyContent:"center",gap:"5px"}}>
                      <span style={{fontSize:"13px"}}>{opt.icon}</span>
                      <span style={{fontSize:"10px",fontWeight:notify===opt.v?"700":"400",
                        color:notify===opt.v?color:"#374151"}}>{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}@keyframes blink{0%,100%{opacity:1}50%{opacity:0}}`}</style>
    </div>
  );
}

// ─── Shared transcript Word/RTF download (same logic as EchoPlayer) ─────────
async function downloadTranscriptWord(txText, filename) {
  const now=new Date(); const pad=n=>String(n).padStart(2,"0");
  const ts=`${now.getFullYear()}${pad(now.getMonth()+1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}`;
  const fileName=filename||`Transcript_${ts}.docx`;

  const escXml=s=>(s||"").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;").replace(/'/g,"&apos;");
  const escRtf=s=>(s||"").replace(/\\/g,"\\\\").replace(/\{/g,"\\{").replace(/\}/g,"\\}").replace(/[^\x00-\x7F]/g,c=>`\\u${c.charCodeAt(0)}\'3f`);

  const lines=(txText||"").split("\n").filter(l=>l.trim());

  const paras=lines.map(line=>{
    const m=line.match(/^(SPEAKER\s+[A-Z]:\s*)(.*)/i);
    if(m) return `<w:p><w:pPr><w:spacing w:before="120" w:after="60"/></w:pPr><w:r><w:rPr><w:b/><w:color w:val="E67E22"/></w:rPr><w:t xml:space="preserve">${escXml(m[1].trim())} </w:t></w:r><w:r><w:rPr><w:sz w:val="22"/></w:rPr><w:t>${escXml(m[2].trim())}</w:t></w:r></w:p>`;
    return `<w:p><w:pPr><w:spacing w:after="60"/></w:pPr><w:r><w:rPr><w:sz w:val="22"/></w:rPr><w:t xml:space="preserve">${escXml(line)}</w:t></w:r></w:p>`;
  });

  const documentXml=`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"
  xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <w:body>${paras.join("\n")}
    <w:sectPr><w:pgSz w:w="12240" w:h="15840"/><w:pgMar w:top="1440" w:right="1440" w:bottom="1440" w:left="1440"/></w:sectPr>
  </w:body>
</w:document>`;

  const stylesXml=`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:style w:type="paragraph" w:styleId="Normal"><w:name w:val="Normal"/><w:rPr><w:sz w:val="24"/><w:szCs w:val="24"/></w:rPr></w:style>
  <w:style w:type="paragraph" w:styleId="Heading1"><w:name w:val="heading 1"/><w:basedOn w:val="Normal"/>
    <w:rPr><w:b/><w:sz w:val="36"/><w:szCs w:val="36"/><w:color w:val="1F3864"/></w:rPr></w:style>
</w:styles>`;

  const relsXml=`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;

  const rootRelsXml=`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`;

  const contentTypesXml=`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
  <Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/>
</Types>`;

  // RTF — works on both local and server, no JSZip needed
  let body="";
  lines.forEach(line=>{
    const m=line.match(/^(SPEAKER\s+[A-Z]:\s*)(.*)/i);
    if(m) body+=`{\\b\\cf1 ${escRtf(m[1].trim())}}\\b0\\cf0  ${escRtf(m[2].trim())}\\par\\par\n`;
    else  body+=escRtf(line)+"\\par\n";
  });
  const rtf=`{\\rtf1\\ansi\\deff0{\\fonttbl{\\f0\\fswiss Arial;}}{\\colortbl;\\red230\\green126\\blue34;}\\f0\\fs24\n${body}}`;
  const blob=new Blob([rtf],{type:"application/rtf"});
  const url=URL.createObjectURL(blob);
  const a=document.createElement("a");
  a.href=url; a.download=fileName.replace(".docx",".rtf");
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(()=>URL.revokeObjectURL(url),1000);
}

// ─── Transcript Panel ─────────────────────────────────────────────────────────
function TranscriptPanel({ transcript, plainText, duration, onClose }) {
  const [view, setView] = useState("speaker");
  const fmtDur = s => { const m=Math.floor(s/60); return `${m}m ${s%60}s`; };
  return(
    <div style={{marginTop:"12px",border:"1px solid #E5E7EB",borderRadius:"10px",
      overflow:"hidden",background:"#fff"}}>
      <div style={{display:"flex",alignItems:"center",gap:"8px",
        padding:"8px 12px",background:"#F8FAFC",borderBottom:"1px solid #E5E7EB"}}>
        <span style={{fontSize:"12px",fontWeight:"700",color:"#111",flex:1}}>
          📝 Transcript {duration?`· ${fmtDur(duration)}`:""}
        </span>
        <button onClick={()=>setView("speaker")}
          style={{padding:"3px 10px",borderRadius:"20px",border:"none",cursor:"pointer",
            background:view==="speaker"?"#6366F1":"#E5E7EB",
            color:view==="speaker"?"#fff":"#6B7280",fontSize:"11px",fontWeight:"600"}}>
          Speakers
        </button>
        <button onClick={()=>setView("plain")}
          style={{padding:"3px 10px",borderRadius:"20px",border:"none",cursor:"pointer",
            background:view==="plain"?"#6366F1":"#E5E7EB",
            color:view==="plain"?"#fff":"#6B7280",fontSize:"11px",fontWeight:"600"}}>
          Plain
        </button>
        <button onClick={()=>downloadTranscriptWord(view==="plain"?plainText:transcript)}
          style={{padding:"3px 10px",borderRadius:"20px",border:"1px solid #E5E7EB",
            background:"#fff",color:"#6B7280",fontSize:"11px",cursor:"pointer",fontWeight:"600"}}>
          ⬇ Word
        </button>
        <button onClick={onClose}
          style={{background:"none",border:"none",cursor:"pointer",
            color:"#9CA3AF",fontSize:"16px",lineHeight:1,padding:"0 2px"}}>×</button>
      </div>
      <div style={{maxHeight:"260px",overflowY:"auto",padding:"10px 14px",
        fontSize:"12px",color:"#374151",lineHeight:1.7,whiteSpace:"pre-wrap"}}>
        {view==="speaker" ? transcript : plainText}
      </div>
    </div>
  );
}

// ─── Generate Transcript Button ───────────────────────────────────────────────
// Works during recording (snapshots current chunks) and after (uses blob)
// Auto-downloads Word/RTF — no panel shown
function GenerateTranscriptBtn({ getBlob, user, color }) {
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState("");

  const generate = async () => {
    setLoading(true); setErr("");
    try {
      const blob = await getBlob(); // getBlob may be async (waits for requestData flush)
      if(!blob || blob.size === 0) { setErr("No audio recorded yet."); setLoading(false); return; }
      const ext  = blob.type.includes("mp4") ? "mp4" : blob.type.includes("wav") ? "wav" : "webm";
      const fd   = new FormData();
      fd.append("userid", user.USERID);
      fd.append("file", blob, `recording.${ext}`);
      const r   = await fetch(`${API}/transcribe`, { method:"POST", headers:{"Auth-Code":user.AUTH_CODE}, body:fd });
      const res = await r.json();
      if(String(res.STATUS)==="1") {
        // Auto-download Word — no panel
        await downloadTranscriptWord(res.TRANSCRIPT||res.PLAIN_TEXT||"");
      } else setErr(res.MESSAGE || "Transcription failed.");
    } catch { setErr("Network error."); }
    setLoading(false);
  };

  return(
    <div>
      <button onClick={generate} disabled={loading}
        style={{padding:"6px 14px",borderRadius:"20px",border:`1.5px solid ${color}`,
          background:"#fff",color:color,cursor:loading?"not-allowed":"pointer",
          fontFamily:"inherit",fontSize:"12px",fontWeight:"700",
          opacity:loading?0.7:1,display:"flex",alignItems:"center",gap:"6px"}}>
        {loading
          ? <><span style={{width:"11px",height:"11px",border:`2px solid ${color}44`,
              borderTopColor:color,borderRadius:"50%",animation:"spin .7s linear infinite",
              display:"inline-block"}}/> Transcribing…</>
          : "📝 Generate Transcript"}
      </button>
      {err&&<div style={{fontSize:"10px",color:"#EF4444",marginTop:"4px"}}>{err}</div>}
    </div>
  );
}


// ─── AudioRecorderWidget — proper React component (not a function call) ───────
function AudioRecorderWidget({ mode, color, onBlob, startTrigger, user }) {
  const [state,   setState]   = useState("idle");
  const [dur,     setDur]     = useState(0);
  const [blobUrl, setBlobUrl] = useState(null);
  const [blob,    setBlob]    = useState(null);
  const [toast,   setToast]   = useState("");
  const [err,     setErr]     = useState("");
  const mrRef     = useRef(null);
  const chunksRef = useRef([]);
  const streamsRef= useRef([]);
  const timerRef  = useRef(null);

  const startRec = async () => {
    setErr(""); setState("requesting");
    streamsRef.current=[];
    try {
      let combined;
      if(mode==="mic"){
        const s=await navigator.mediaDevices.getUserMedia({audio:{echoCancellation:true,noiseSuppression:true,sampleRate:44100}});
        streamsRef.current=[s]; combined=s;
      } else if(mode==="system"){
        const s=await navigator.mediaDevices.getDisplayMedia({video:true,audio:{echoCancellation:false,noiseSuppression:false,sampleRate:44100}});
        streamsRef.current=[s];
        const at=s.getAudioTracks();
        if(!at.length) throw new Error("No audio — enable 'Share tab audio'.");
        s.getVideoTracks().forEach(t=>t.stop());
        combined=new MediaStream(at);
      } else {
        const mic=await navigator.mediaDevices.getUserMedia({audio:{echoCancellation:true,noiseSuppression:true,sampleRate:44100}});
        streamsRef.current=[mic];
        let sys=null;
        try{ sys=await navigator.mediaDevices.getDisplayMedia({video:true,audio:{echoCancellation:false,noiseSuppression:false,sampleRate:44100}}); streamsRef.current.push(sys); }catch(e){}
        if(sys){
          const sat=sys.getAudioTracks(); sys.getVideoTracks().forEach(t=>t.stop());
          if(sat.length){
            const ctx=new AudioContext();
            const m=ctx.createMediaStreamSource(mic);
            const s=ctx.createMediaStreamSource(new MediaStream(sat));
            const d=ctx.createMediaStreamDestination();
            m.connect(d); s.connect(d); combined=d.stream;
          } else combined=mic;
        } else combined=mic;
      }
      chunksRef.current=[];
      const fmt=bestAudioFmt();
      let mr; try{ mr=new MediaRecorder(combined,fmt.mimeType?fmt:{}); }catch{ mr=new MediaRecorder(combined); }
      mr.ondataavailable=e=>{ if(e.data.size>0) chunksRef.current.push(e.data); };
      mr.onstop=()=>{
        const b=new Blob(chunksRef.current,{type:mr.mimeType||"audio/webm"});
        const url=URL.createObjectURL(b);
        setBlob(b); setBlobUrl(url); onBlob(b);
        setState("done"); clearInterval(timerRef.current);
        streamsRef.current.forEach(s=>s.getTracks().forEach(t=>t.stop()));
        // auto-download
        try{
          const a=document.createElement("a");
          const ts=new Date().toISOString().replace(/[:.]/g,"-").slice(0,19);
          const ext=(mr.mimeType||"").includes("mp4")?"mp4":"webm";
          a.href=url; a.download=`echo_recording_${ts}.${ext}`;
          document.body.appendChild(a); a.click(); document.body.removeChild(a);
          setToast("✓ Recording downloaded"); setTimeout(()=>setToast(""),3000);
        }catch(e){}
      };
      mr.start(1000); mrRef.current=mr;
      setDur(0); setState("recording");
      timerRef.current=setInterval(()=>setDur(d=>d+1),1000);
      combined.getTracks().forEach(t=>t.addEventListener("ended",()=>{ if(mrRef.current?.state!=="inactive") mrRef.current?.stop(); }));
    } catch(e){
      setErr(e.name==="NotAllowedError"?"Permission denied.":e.message);
      setState("idle");
      streamsRef.current.forEach(s=>s.getTracks().forEach(t=>t.stop()));
    }
  };

  // expose startRec via ref so mode button can call it
  useEffect(()=>{ if(startTrigger) startTrigger.current=startRec; },[]);

  const pauseRec  = ()=>{ mrRef.current?.pause();  setState("paused");    clearInterval(timerRef.current); };
  const resumeRec = ()=>{ mrRef.current?.resume(); setState("recording"); timerRef.current=setInterval(()=>setDur(d=>d+1),1000); };
  const stopRec   = ()=>{ if(mrRef.current?.state!=="inactive") mrRef.current?.stop(); clearInterval(timerRef.current); };
  const reset     = ()=>{
    streamsRef.current.forEach(s=>s.getTracks().forEach(t=>t.stop()));
    if(mrRef.current?.state!=="inactive") mrRef.current?.stop();
    clearInterval(timerRef.current);
    setBlob(null); setBlobUrl(null); onBlob(null); setState("idle"); setDur(0); setErr(""); setToast("");
  };

  return(
    <div style={{background:"#F8FAFC",borderRadius:"12px",padding:"20px",
      border:"1px solid #E5E7EB",textAlign:"center"}}>
      {err&&<div style={{color:"#B91C1C",fontSize:"12px",padding:"8px 12px",
        background:"#FEF2F2",borderRadius:"8px",marginBottom:"12px"}}>{err}</div>}
      {state==="requesting"&&(
        <div style={{color:"#6B7280",fontSize:"13px"}}>
          <div style={{width:"20px",height:"20px",border:`2px solid ${color}33`,
            borderTopColor:color,borderRadius:"50%",animation:"spin .8s linear infinite",
            margin:"0 auto 10px"}}/>
          Requesting permission…
        </div>
      )}
      {(state==="recording"||state==="paused")&&(
        <div>
          <div style={{fontSize:"40px",fontWeight:"800",letterSpacing:"3px",
            fontVariantNumeric:"tabular-nums",marginBottom:"8px",
            color:state==="recording"?"#EF4444":"#9CA3AF"}}>
            {state==="recording"&&<span style={{display:"inline-block",width:"11px",height:"11px",
              borderRadius:"50%",background:"#EF4444",marginRight:"10px",
              animation:"blink 1s infinite",verticalAlign:"middle"}}/>}
            {fmtTime(dur)}
          </div>
          <div style={{display:"flex",gap:"10px",justifyContent:"center",flexWrap:"wrap"}}>
            {state==="recording"
              ?<button onClick={pauseRec}
                  style={{padding:"9px 22px",borderRadius:"22px",border:"none",
                    background:"#FEF3C7",color:"#92400E",cursor:"pointer",
                    fontFamily:"inherit",fontSize:"13px",fontWeight:"600"}}>⏸ Pause</button>
              :<button onClick={resumeRec}
                  style={{padding:"9px 22px",borderRadius:"22px",border:"none",
                    background:`${color}15`,color,cursor:"pointer",
                    fontFamily:"inherit",fontSize:"13px",fontWeight:"600"}}>▶ Resume</button>
            }
            <button onClick={stopRec}
              style={{padding:"9px 22px",borderRadius:"22px",border:"none",
                background:"#FEE2E2",color:"#991B1B",cursor:"pointer",
                fontFamily:"inherit",fontSize:"13px",fontWeight:"600"}}>⏹ Stop</button>
          </div>
          {/* Generate Transcript during recording — snapshots current chunks */}
          {user&&chunksRef.current.length>0&&(
            <div style={{marginTop:"10px",display:"flex",justifyContent:"center"}}>
              <GenerateTranscriptBtn
                getBlob={()=>new Promise(resolve=>{
                  // flush latest buffer then snapshot — gives accurate audio up to this moment
                  const mr=mrRef.current;
                  if(mr&&mr.state!=="inactive"){
                    const prev=mr.ondataavailable;
                    mr.ondataavailable=e=>{
                      if(e.data.size>0) chunksRef.current.push(e.data);
                      mr.ondataavailable=prev; // restore original handler
                      resolve(new Blob(chunksRef.current,{type:mr.mimeType||"audio/webm"}));
                    };
                    try{ mr.requestData(); }catch{ mr.ondataavailable=prev; resolve(new Blob(chunksRef.current,{type:mr.mimeType||"audio/webm"})); }
                  } else {
                    resolve(new Blob(chunksRef.current,{type:"audio/webm"}));
                  }
                })}
                user={user} color={color}/>
            </div>
          )}
        </div>
      )}
      {toast&&(
        <div style={{padding:"8px 12px",borderRadius:"8px",background:"#059669",
          color:"#fff",fontSize:"12px",fontWeight:"600",marginBottom:"8px"}}>
          {toast}
        </div>
      )}
      {state==="done"&&blobUrl&&(
        <div>
          <div style={{fontSize:"13px",color:"#059669",fontWeight:"700",marginBottom:"10px"}}>
            ✓ Ready · {fmtTime(dur)}</div>
          <audio controls src={blobUrl} style={{width:"100%",marginBottom:"10px"}}/>
          {user&&blob&&(
            <div style={{marginBottom:"8px"}}>
              <GenerateTranscriptBtn
                getBlob={()=>Promise.resolve(blob)}
                user={user} color={color}/>
            </div>
          )}
          <div style={{display:"flex",gap:"8px"}}>
            <button onClick={()=>{
                const a=document.createElement("a");
                const ts=new Date().toISOString().replace(/[:.]/g,"-").slice(0,19);
                const ext=blob?.type?.includes("mp4")?"mp4":"webm";
                a.href=blobUrl; a.download=`echo_recording_${ts}.${ext}`;
                document.body.appendChild(a); a.click(); document.body.removeChild(a);
                setToast("✓ Downloaded"); setTimeout(()=>setToast(""),2500);
              }}
              style={{padding:"6px 14px",borderRadius:"20px",border:"none",
                background:color,color:"#fff",cursor:"pointer",
                fontFamily:"inherit",fontSize:"12px",fontWeight:"700"}}>
              ⬇ Download again
            </button>
            <button onClick={reset}
              style={{padding:"6px 14px",borderRadius:"20px",border:"none",
                background:"#F3F4F6",color:"#6B7280",cursor:"pointer",
                fontFamily:"inherit",fontSize:"12px"}}>🔄 Re-record</button>
          </div>
        </div>
      )}
    </div>
  );
}